import { TouchEvent, useCallback, useRef } from 'react'
import { useObservable } from 'react-use'
import { NEVER, Subject, fromEvent, of } from 'rxjs'
import {
  delay,
  distinctUntilChanged,
  exhaustMap,
  finalize,
  map,
  repeat,
  share,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators'

import { disableScroll, enableScroll, isServer } from '../../../lib/utils'

type PreventScrollOnSwipe = (event: TouchEvent<HTMLDivElement>) => void

const reEnableScrollDelayMilliseconds = 300

export function usePreventScrollOnSwipe(): PreventScrollOnSwipe {
  const touchStart$ = useRef(new Subject<TouchEvent<HTMLDivElement>>()).current
  const touchEnd$ = useRef(
    isServer() ? NEVER : fromEvent<TouchEvent<HTMLDivElement>>(window, 'touchend')
  ).current
  const touchMove$ = useRef(
    isServer() ? NEVER : fromEvent<TouchEvent<HTMLDivElement>>(window, 'touchmove')
  ).current

  const isHorizontalSwipe$ = useRef(
    // Start inspecting touchmove events upon a touchstart event on the carousel:
    touchStart$.pipe(
      exhaustMap(touchStartEvent =>
        touchMove$.pipe(
          // Determine if the touchmove is a horizontal swipe and map to a boolean accordingly:
          map(touchMoveEvent => {
            const horizontalSwipeDistance: number =
              touchMoveEvent.touches[0]!.clientX - touchStartEvent.touches[0]!.clientX

            const verticalSwipeDistance: number =
              touchMoveEvent.touches[0]!.clientY - touchStartEvent.touches[0]!.clientY

            const verticalToHorizontalRatio = Math.abs(
              verticalSwipeDistance / horizontalSwipeDistance
            )

            /**
             * The vertical swipe distance must be at least `verticalScrollThresholdScalar`
             * times the horizontal swipe distance to be considered a vertical scroll.
             * Otherwise the swipe is considered a horizontal carousel swipe.
             *
             * The higher the value of `verticalScrollThresholdScalar`, the more tightly
             * "up or down" the user must swipe to achieve a vertical scroll, and therefore
             * the more likely to result in a page-scroll-locked horizontal carousel swipe.
             */
            const verticalScrollThresholdScalar = 3

            const isHorizontalScroll: boolean =
              verticalToHorizontalRatio < verticalScrollThresholdScalar

            return isHorizontalScroll
          })
        )
      ),
      // Don't emit events with the same boolean value:
      distinctUntilChanged(),
      // Introduce a delay in reenabling vertical scroll to combat scroll momentum:
      switchMap(isHorizontalSwipe =>
        of(isHorizontalSwipe).pipe(delay(isHorizontalSwipe ? 0 : reEnableScrollDelayMilliseconds))
      ),
      // Perform the side-effect of enabling/disabling screen scroll depending on boolean:
      tap(isHorizontalSwipe => {
        if (isHorizontalSwipe) {
          disableScroll()
        } else {
          enableScroll()
        }
      }),
      // Stop listening to touchmove events once a touchend event has occurred after touchstart:
      takeUntil(touchStart$.pipe(switchMap(() => touchEnd$))),
      // Introduce a delay in reenabling vertical scroll to combat scroll momentum:
      delay(reEnableScrollDelayMilliseconds),
      // Tidy up by reenabling scroll once the user is no longer swiping:
      finalize(() => {
        enableScroll()
      }),
      // Allow multiple subscriptions to the same Observable:
      share(),
      // Handle subsequent touchstart -> touchmove events:
      repeat()
    )
  ).current

  useObservable(isHorizontalSwipe$)

  const preventScrollOnSwipe = useCallback((event: TouchEvent<HTMLDivElement>) => {
    touchStart$.next(event)
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return preventScrollOnSwipe
}
